import { useEffect, useState, type ReactNode, type Ref } from "react";
import { Download, Loader2, X } from "lucide-react";
import { ROL_ETIKET, yetkiMetni, yetkiliMi, type Islem } from "@oasis/domain";
import { useOturum } from "@/lib/oturum";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { bicimDili, cevir } from "@/lib/dil";

/** Prototipteki .wall / .note / .okmsg: kural görünürdür (spec: kalıcı desen). */
export function Band({ tip, children, className }: { tip: "duvar" | "not" | "ok" | "bilgi"; children: ReactNode; className?: string }) {
  const s = { duvar: "bg-bad-bg text-bad border-bad/20", not: "bg-warn-bg text-warn border-warn/20", ok: "bg-ok-bg text-ok border-ok/20", bilgi: "bg-info-bg text-info border-info/20" }[tip];
  return <div className={cn("rounded-md border px-3 py-2 text-sm leading-relaxed", s, className)}>{children}</div>;
}

export function Sayfa({ baslik, alt, ust, children, aksiyon }: { baslik: ReactNode; alt?: ReactNode; ust?: ReactNode; children: ReactNode; aksiyon?: ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          {ust && <div className="text-xs font-semibold uppercase tracking-[0.1em] text-ink-3">{ust}</div>}
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{baslik}</h1>
          {alt && <p className="mt-0.5 max-w-4xl text-base leading-relaxed text-ink-2">{alt}</p>}
        </div>
        {aksiyon}
      </div>
      {children}
    </div>
  );
}

export function Kutu({ baslik, aksiyon, children, className, padded = true }: { baslik?: ReactNode; aksiyon?: ReactNode; children: ReactNode; className?: string; padded?: boolean }) {
  return (
    <section className={cn("min-w-0 rounded-lg border border-line bg-surface shadow-[var(--shadow)]", className)}>
      {(baslik || aksiyon) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="text-base font-semibold tracking-tight">{baslik}</h2>
          {aksiyon && <div className="text-sm text-ink-2">{aksiyon}</div>}
        </header>
      )}
      <div className={cn("text-base", padded && "p-4")}>{children}</div>
    </section>
  );
}

export function Etiket({ tone = "neutral", children, className }: { tone?: "neutral" | "info" | "ok" | "warn" | "bad" | "hold"; children: ReactNode; className?: string }) {
  const s = { neutral: "bg-surface-2 text-ink-2", info: "bg-info-bg text-info", ok: "bg-ok-bg text-ok", warn: "bg-warn-bg text-warn", bad: "bg-bad-bg text-bad", hold: "bg-hold-bg text-hold" }[tone];
  return <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium leading-4", s, className)}>{children}</span>;
}

export function Cip({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("mr-1 inline-block rounded-full bg-surface-2 px-2 py-0.5 text-xs text-ink-2", className)}>{children}</span>;
}

/** Etiket + değer çifti: künye/özet ızgaralarında hep aynı hizada dursun diye tek bileşen. */
export function Kv({ ad, children, className }: { ad: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="text-xs text-ink-3">{ad}</div>
      <div className="text-sm text-ink">{children}</div>
    </div>
  );
}

/** Künye ızgarası: Kv'leri eşit sütunlara yayar (kart içi özet blokları). */
export function KvIzgara({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3", className)}>{children}</div>;
}

export function Damga({ kim, ts }: { kim?: string | null; ts?: string | Date | null }) {
  if (!kim) return null;
  return <span className="block text-xs text-ink-3">{kim} · {ts ? new Date(ts).toLocaleString(bicimDili(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>;
}

/**
 * Yetkiye bağlı buton: yetkisizse pasif + tooltip (prototip yetkiTitle).
 * `mesgul` verilirse dönen bir gösterge çıkar ve buton kilitlenir: uzun süren yükleme/indirme
 * işlerinde kullanıcı işin sürdüğünü görsün, ikinci kez tıklamasın.
 * `basili` (D-073): seçmeli düğme (Same / Different, durum segmenti, süzgeç) `aria-pressed` taşır; verilmezse öznitelik yazılmaz.
 * `etiket` (D-073 inceleme turu): ekran okuyucunun adı (`aria-label`; tekrarlanan düğmede parça ya da satır adıyla). `ref`, `onPointerDown`:
 * odağı geri vermek ve alandan çıkışı ayırt etmek için (söküm formunun "Different" alanı).
 */
export function Buton({ islem, onClick, children, tur = "normal", disabled, mesgul, title, className, kucuk, basili, etiket, ref, onPointerDown }: { islem?: Islem; onClick?: () => void | Promise<void>; children: ReactNode; tur?: "normal" | "birincil" | "tehlike"; disabled?: boolean; mesgul?: boolean; title?: string; className?: string; kucuk?: boolean; basili?: boolean; etiket?: string; ref?: Ref<HTMLButtonElement>; onPointerDown?: () => void }) {
  const { ben } = useOturum();
  const yetkisiz = islem && ben ? !yetkiliMi(ben.rol, islem) : false;
  const ipucu = yetkisiz && islem ? yetkiMetni(islem, (r) => ROL_ETIKET[r]) : title;
  const s = { normal: "border-line bg-surface text-ink hover:border-line-strong", birincil: "border-orange bg-orange text-white hover:opacity-90", tehlike: "border-bad/30 bg-bad-bg text-bad hover:border-bad" }[tur];
  return (
    <button ref={ref} type="button" onClick={onClick} onPointerDown={onPointerDown} disabled={disabled || yetkisiz || mesgul} title={ipucu} aria-label={etiket} aria-busy={mesgul || undefined} aria-pressed={basili} className={cn("inline-flex items-center gap-1.5 rounded-md border font-medium transition disabled:cursor-not-allowed disabled:opacity-45", kucuk ? "h-kontrol-kucuk px-2.5 text-sm" : "h-kontrol px-3 text-base", s, className)}>
      {mesgul && <Loader2 size={kucuk ? 13 : 15} className="animate-spin" />}
      {children}
    </button>
  );
}

/**
 * `kilitli` ise (ör. yükleme sürerken) Esc, dış tıklama ve kapat düğmesi çalışmaz.
 * `genis`: yok → sm:max-w-xl · true → sm:max-w-3xl · "cok" → sm:max-w-5xl (1024 px; belge ve sayım pencereleri, D-045/D-048).
 */
export function Modal({ baslik, onKapat, children, genis, kilitli }: { baslik: ReactNode; onKapat: () => void; children: ReactNode; genis?: boolean | "cok"; kilitli?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && !kilitli && onKapat();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onKapat, kilitli]);
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4" onClick={() => !kilitli && onKapat()}>
      <div className={cn("max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-surface p-4 shadow-[var(--shadow-lg)] sm:rounded-xl sm:p-5", genis === "cok" ? "sm:max-w-5xl" : genis ? "sm:max-w-3xl" : "sm:max-w-xl")} onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-md font-semibold">{baslik}</h2>
          <button onClick={onKapat} disabled={kilitli} className="rounded-md p-1 text-ink-3 hover:bg-surface-2 disabled:opacity-40" aria-label={cevir("Close")}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Tek girdi biçimi: sabit yükseklik (h-kontrol) sayesinde buton ve seçicilerle aynı satırda hizalanır. */
export const girdi = "h-kontrol w-full rounded-md border border-line-strong bg-bg px-2.5 text-sm text-ink outline-none transition focus:border-orange disabled:opacity-60";
/** Çok satırlı alan: yükseklik içeriğe göre, diğer ölçüler girdiyle aynı. */
export const alan = "w-full rounded-md border border-line-strong bg-bg px-2.5 py-2 text-sm text-ink outline-none transition focus:border-orange disabled:opacity-60";
/** Alan etiketi: her formda aynı boyut ve boşluk. */
export const etiket = "mb-1 block text-xs font-medium text-ink-3";
export const tabloBas = "whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-ink-3";
export const tabloHucre = "px-3 py-2.5 align-top text-sm";

/** Ekran görünümünü Excel olarak indirir (kullanıcının gördüğü sütunlar). */
export function ExcelButonu({ gorunum, etiket = "Excel" }: { gorunum: string; etiket?: string }) {
  const [mesgul, setMesgul] = useState(false);
  return (
    <Buton
      kucuk
      mesgul={mesgul}
      title={cevir("Download this list as Excel")}
      onClick={async () => {
        setMesgul(true);
        try {
          await api.indir(`/disa-aktar/gorunum/${gorunum}.xlsx`);
        } finally {
          setMesgul(false);
        }
      }}
    >
      {!mesgul && <Download size={13} />} {mesgul ? cevir("preparing...") : etiket}
    </Buton>
  );
}

export function Hata({ mesaj }: { mesaj: string | null }) {
  return mesaj ? <Band tip="duvar">{mesaj}</Band> : null;
}

export function Bos({ children }: { children: ReactNode }) {
  return <p className="px-1 py-3 text-base text-ink-3">{children}</p>;
}
